import { useEffect, useState } from 'react';
import {
  itsmSlaApi,
  type ItsmItem, type ItsmLevel, type ItsmPriority, type ItsmSlaClock, type ItsmSlaPolicy,
} from '../../services/api';

/**
 * ITIL priority and SLA logic for the ITSM UI (EP-27 FE-6, PO46-1308): the
 * policy fetch, the matrix preview, clock states and the Raise payload. Kept
 * apart from the components so they stay fast-refreshable.
 */

export const LEVELS: ItsmLevel[] = ['high', 'medium', 'low'];
export const PRIORITIES: ItsmPriority[] = ['critical', 'high', 'medium', 'low'];

// One fetch per page load: the matrix changes rarely, and every card and form
// on the page would otherwise ask for it.
let policyPromise: Promise<ItsmSlaPolicy | null> | null = null;

export function useSlaPolicy(): ItsmSlaPolicy | null {
  const [pol, setPol] = useState<ItsmSlaPolicy | null>(null);
  useEffect(() => {
    if (!policyPromise) {
      policyPromise = itsmSlaApi.policy().catch(() => {
        policyPromise = null;           // a later mount retries
        return null;
      });
    }
    let live = true;
    policyPromise.then((p) => { if (live) setPol(p); });
    return () => { live = false; };
  }, []);
  return pol;
}

/** The matrix's answer, with the server's own fallback: one axis set means the
 *  other is taken as medium. '' when neither is set or the matrix is unknown. */
export function computePriority(pol: ItsmSlaPolicy | null, impact: string,
                                urgency: string): ItsmPriority | '' {
  if (!pol || (!impact && !urgency)) return '';
  const row = pol.matrix?.[(impact || 'medium') as ItsmLevel];
  return row?.[(urgency || 'medium') as ItsmLevel] || '';
}

function fmtDuration(secs: number): string {
  const s = Math.abs(Math.round(secs));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${m}m`;
  return `${Math.max(m, 1)}m`;
}

/** "in 2h 10m" / "35m ago". */
export function relTime(iso: string, now = Date.now()): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const secs = (t - now) / 1000;
  return secs >= 0 ? `in ${fmtDuration(secs)}` : `${fmtDuration(secs)} ago`;
}

export type ClockState = 'met' | 'met-late' | 'breached' | 'paused' | 'warning' | 'within';

/** Mirrors services/itsm_sla.status(), so the board and the panel agree with
 *  the dispatcher about what "due soon" and "breached" mean. */
export function clockState(clock: ItsmSlaClock | undefined, pausedSince: string | undefined,
                           warnAt: number, now = Date.now()): ClockState | null {
  if (!clock || !clock.dueAt) return null;
  const due = Date.parse(clock.dueAt);
  if (clock.metAt) {
    return clock.breached || Date.parse(clock.metAt) > due ? 'met-late' : 'met';
  }
  if (clock.breached || (now > due && !pausedSince)) return 'breached';
  if (pausedSince) return 'paused';
  const target = (clock.targetMinutes || 0) * 60;
  const elapsed = (now - Date.parse(clock.startedAt)) / 1000 - (clock.pausedSeconds || 0);
  if (target && elapsed >= warnAt * target) return 'warning';
  return 'within';
}

/** The worst open clock on a ticket, for a board card. */
export function ticketSlaState(item: ItsmItem, warnAt: number): 'breached' | 'warning' | null {
  if (item.slaBreached) return 'breached';
  const sla = item.sla;
  if (!sla) return null;
  const states = [sla.response, sla.resolution].map(
    (c) => clockState(c, sla.pausedSince, warnAt));
  if (states.includes('breached')) return 'breached';
  if (states.includes('warning')) return 'warning';
  return null;
}

export interface RaisePriorityValue {
  impact: string;
  urgency: string;
  override: boolean;
  priority: string;
  reason: string;
}

export const EMPTY_RAISE_PRIORITY: RaisePriorityValue = {
  impact: '', urgency: '', override: false, priority: '', reason: '',
};

/** Whether the override needs a reason: it differs from the matrix's answer. */
export function overrideDiffers(pol: ItsmSlaPolicy | null, v: RaisePriorityValue): boolean {
  const computed = computePriority(pol, v.impact, v.urgency);
  return v.override && !!computed && !!v.priority && v.priority !== computed;
}

/** What the Raise form sends: impact/urgency when assessed, and a priority only
 *  when the user overrode it. Returns an error string instead when an override
 *  that differs from the matrix has no reason. */
export function raisePriorityPayload(pol: ItsmSlaPolicy | null,
                                     v: RaisePriorityValue): Partial<ItsmItem> | string {
  const out: Partial<ItsmItem> = {};
  if (v.impact) out.impact = v.impact as ItsmLevel;
  if (v.urgency) out.urgency = v.urgency as ItsmLevel;
  if (v.override && v.priority) {
    const computed = computePriority(pol, v.impact, v.urgency);
    if (computed && v.priority !== computed) {
      if (!v.reason.trim()) return 'An override needs a reason';
      out.priority = v.priority as ItsmPriority;
      out.priorityReason = v.reason.trim();
    } else if (!computed) {
      // No matrix answer to differ from: a plain manual priority.
      out.priority = v.priority as ItsmPriority;
      if (v.reason.trim()) out.priorityReason = v.reason.trim();
    }
    // Same as the matrix: nothing to send, the server computes it.
  }
  return out;
}
